import React, { useEffect, useMemo, useState } from "react";
import { DownloadIcon } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import styles from "./TableING.module.css";
import INGStyles from "../ING.module.css";
import { useINGContextData } from "../Context";
import { getIngReportData } from "../ingService";
import { displayValue } from "../../../../Shared/common";
import ChartLoader from "../../../../Shared/Loader";

const POLL_INTERVAL_MS = 15000;
const PAGE_SIZE = 20;

function BoolBadge({ value }) {
  const label = String(value ?? "").toUpperCase();
  if (label !== "TRUE" && label !== "FALSE")
    return <span>{displayValue(value)}</span>;
  return (
    <span
      className={`${styles.badge} ${label === "TRUE" ? styles.badgeTrue : styles.badgeFalse}`}
    >
      {label}
    </span>
  );
}

function StatusBadge({ value }) {
  const label = String(value ?? "").toUpperCase();
  if (label !== "VALID") return <span>{displayValue(value)}</span>;
  return (
    <span className={`${styles.badge} ${styles.badgeValid}`}>{label}</span>
  );
}

// Seconds -> "HH:MM:SS" (negative values keep a leading "-"); returns the
// input untouched when it isn't numeric.
function formatDuration(value) {
  if (value === null || value === undefined || value === "") return value;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  const total = Math.round(Math.abs(n));
  const pad = (x) => String(x).padStart(2, "0");
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  return `${n < 0 ? "-" : ""}${pad(h)}:${pad(m)}:${pad(sec)}`;
}

// Shapes a raw ingReport row into the column names/order the downloaded CSV
// should use — distinct from the on-screen table's headers.
const mapRowForCsv = (row) => ({
  City: row["City"],
  Depot: row["Depot"],
  Contract: row["Contract"],
  "Registration Number": row["Register Number"],
  "Vehicle ID": row["Vehicle ID"],
  "Current Timestamp": row["Current Date"],
  "Ascent Received": row["Latest Data Received Time"],
  "Latest L1 Sent Time": row["Latest L1 Sent Time"],
  "Latest L3 Received Time": row["Latest L3 Received Time"],
  "Time Difference(HH:MM:SS)": formatDuration(row["L3 Delay Seconds"]),
  "L1_L3 Delay(HH:MM:SS)": formatDuration(row["L1_L3 Delay"]),
  "Current Odometer": row["Current Odometer"],
  "Vehicle Speed": row["Vehicle Speed"],
  "Engine Status": row["Engine Status"] === 1 ? "ON" : "OFF",
  "Network Signal Strength": row["Network Signal Strength"],
  "Packet Status": row["Packet Status"],
  "GPS Status": row["GPS Status"],
  "Ignition Status": row["Ignition Status"] === 1 ? "ON" : "OFF",
  "Network Provider": row["Network Provider"],
});

// Numeric value of a delay field from the API row, or null if
// missing/non-numeric.
function getDelayValue(row, key) {
  const v = row[key];
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

// Delay cell: red when negative or above 15, green otherwise (blank if no
// numeric value).
function DelayValue({ value }) {
  if (value === null || value === undefined || value === "")
    return <span>{displayValue(value)}</span>;
  const n = Number(value);
  if (Number.isNaN(n)) return <span>{displayValue(value)}</span>;
  const bad = n < 0 || n > 15;
  return (
    <span className={bad ? styles.delayRed : styles.delayGreen}>
      {formatDuration(value)}
    </span>
  );
}

function OnOffBadge({ value }) {
  const label = String(value ?? "").toUpperCase();
  const isOn = value === true || label === "ON" || label === "TRUE";
  const isOff = value === false || label === "OFF" || label === "FALSE";
  if (!isOn && !isOff) return <span>{displayValue(value)}</span>;
  return (
    <span
      className={`${styles.badge} ${isOn ? styles.badgeTrue : styles.badgeFalse}`}
    >
      {isOn ? "ON" : "OFF"}
    </span>
  );
}

export default function TableING({ title = "ING Report", subtitle }) {
  const { GlobalFilter, setGlobalFilters } = useINGContextData();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState("");
  const [currentDate, setCurrentDate] = useState("");
  const [page, setPage] = useState(1);
  // One active sort at a time: Time Difference (L3 Delay Seconds) or
  // L1_L3 Delay, always "asc" or "desc"; defaults to Time Difference desc.
  const [sort, setSort] = useState({ key: "L3 Delay Seconds", dir: "desc" });

  // Engine/Ignition Status live in GlobalFilter (not local state) so the
  // filter bar's Reset button can clear them too.
  const engineStatusFilter = GlobalFilter.engineStatus || "";
  const ignitionStatusFilter = GlobalFilter.ignitionStatus || "";

  const requestBody = {
    ...GlobalFilter,
    engine_status: engineStatusFilter,
    ignition_status: ignitionStatusFilter,
  };

  // Refetch on every filter change (global filters + the Engine/Ignition
  // column dropdowns), then keep polling every 15s in the background so the
  // table stays live without a full-page spinner.
  useEffect(() => {
    let cancelled = false;
    let isFirstFetch = true;

    const fetchData = () => {
      if (isFirstFetch) setIsLoading(true);

      getIngReportData(requestBody)
        .then((res) => {
          if (cancelled) return;
          setRows(res?.data || []);
          setLastRefresh(res?.refreshDate || "");
          setCurrentDate(res?.data?.[0]?.["Current Date"] || "");
        })
        .catch(() => {
          if (cancelled) return;
          setRows([]);
          setLastRefresh("");
          setCurrentDate("");
        })
        .finally(() => {
          if (cancelled || !isFirstFetch) return;
          setIsLoading(false);
          isFirstFetch = false;
        });
    };

    fetchData();
    const intervalId = setInterval(fetchData, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GlobalFilter]);

  // A filter change should land back on page 1; a background poll should not.
  useEffect(() => {
    setPage(1);
  }, [GlobalFilter]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  // Sort the full result set (not just the visible page); rows without a
  // numeric value in the sorted column always go last.
  const sortedRows = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((x, y) => {
      const a = getDelayValue(x, sort.key);
      const b = getDelayValue(y, sort.key);
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return (a - b) * dir;
    });
  }, [rows, sort]);

  const pagedRows = useMemo(
    () => sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sortedRows, page],
  );

  // Clicking the active column flips direction; a new column starts at desc.
  const toggleSort = (key) => {
    setSort((s) =>
      s.key === key
        ? { key, dir: s.dir === "desc" ? "asc" : "desc" }
        : { key, dir: "desc" },
    );
    setPage(1);
  };

  const sortArrow = (key) =>
    sort.key !== key ? "⇅" : sort.dir === "asc" ? "▲" : "▼";

  // Green when the gap between Current Date (from the rows) and refreshDate
  // is within the poll interval, red when it is larger.
  const isRefreshFresh = useMemo(() => {
    const toMs = (s) => new Date(String(s).replace(" ", "T")).getTime();
    const diff = Math.abs(toMs(currentDate) - toMs(lastRefresh));
    return !Number.isNaN(diff) && diff <= POLL_INTERVAL_MS;
  }, [currentDate, lastRefresh]);

  const rangeStart = rows.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = Math.min(page * PAGE_SIZE, rows.length);

  // requestBody already carries the last-7-days range from GlobalFilter.
  const handleDownload = () => {
    getIngReportData(requestBody)
      .then((res) => {
        const data = res?.data || [];
        if (!data.length) return;

        const worksheet = XLSX.utils.json_to_sheet(data.map(mapRowForCsv));
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, "ing_report.csv");
      })
      .catch(() => {});
  };

  return (
    <div className={`card mx-3 mt-2 mb-3 shadow-sm ${styles.card}`}>
      <div className="card-body px-3 py-2">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <div className={INGStyles.titleWrap}>
            <span className={INGStyles.titleBar} />
            <span className={INGStyles.title}>{title}</span>
            {subtitle && <span className={INGStyles.subtitle}>{subtitle}</span>}
            {lastRefresh && (
              <span className="ms-3 text-muted" style={{ fontSize: "0.85rem" }}>
                <span
                  className={`${styles.refreshDot} ${isRefreshFresh ? styles.refreshDotGreen : styles.refreshDotRed}`}
                />
                Last Refresh: {lastRefresh}
              </span>
            )}
          </div>

          <button
            className={`fleet-btn fleet-btn-sm blue-theme custom-Btn-animation ${styles.downloadBtn}`}
            type="button"
            onClick={handleDownload}
          >
            <DownloadIcon size={14} /> Download
          </button>
        </div>

        <div className={`table-responsive ${styles.tableWrapper}`}>
          <table
            className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable}`}
          >
            <thead>
              <tr>
                <th>Register Number</th>
                <th>Vehicle ID</th>
                <th>Current Timestamp</th>
                {/* <th>Ascent Received</th> */}
                <th>Latest L1 Sent Time</th>
                <th>Latest L3 Received Time</th>
                <th
                  onClick={() => toggleSort("L3 Delay Seconds")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  Time Difference(HH:MM:SS) {sortArrow("L3 Delay Seconds")}
                </th>
                <th
                  onClick={() => toggleSort("L1_L3 Delay")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                  title="Click to sort"
                >
                  L1_L3 Delay(HH:MM:SS) {sortArrow("L1_L3 Delay")}
                </th>
                <th>Current Odometer</th>
                <th>Vehicle Speed</th>
                <th>
                  <div className={styles.thFilterWrap}>
                    <span>Engine Status</span>
                    <select
                      className={styles.thFilterSelect}
                      value={engineStatusFilter}
                      onChange={(e) =>
                        setGlobalFilters((prev) => ({
                          ...prev,
                          engineStatus: e.target.value,
                        }))
                      }
                    >
                      <option value="">--Select option--</option>
                      <option value="ON">ON</option>
                      <option value="OFF">OFF</option>
                    </select>
                  </div>
                </th>
                <th>Network Signal Strength</th>
                <th>Packet Status</th>
                <th>GPS Status</th>
                <th>
                  <div className={styles.thFilterWrap}>
                    <span>Ignition Status</span>
                    <select
                      className={styles.thFilterSelect}
                      value={ignitionStatusFilter}
                      onChange={(e) =>
                        setGlobalFilters((prev) => ({
                          ...prev,
                          ignitionStatus: e.target.value,
                        }))
                      }
                    >
                      <option value="">--Select option--</option>
                      <option value="ON">ON</option>
                      <option value="OFF">OFF</option>
                    </select>
                  </div>
                </th>
                <th>Network Provider</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={16}>
                    <ChartLoader top="60%" />
                  </td>
                </tr>
              ) : pagedRows?.length ? (
                pagedRows.map((row, idx) => (
                  <tr key={row["Vehicle ID"] ?? idx}>
                    <td>{displayValue(row["Register Number"])}</td>
                    <td>{displayValue(row["Vehicle ID"])}</td>
                    <td>{displayValue(row["Current Date"])}</td>
                    {/* <td>{displayValue(row["Latest Data Received Time"])}</td> */}
                    <td>{displayValue(row["Latest L1 Sent Time"])}</td>
                    <td>{displayValue(row["Latest L3 Received Time"])}</td>
                    <td>
                      <DelayValue value={row["L3 Delay Seconds"]} />
                    </td>
                    <td>
                      <DelayValue value={row["L1_L3 Delay"]} />
                    </td>
                    <td>{displayValue(row["Current Odometer"])}</td>
                    <td>{displayValue(row["Vehicle Speed"])}</td>
                    <td>
                      <OnOffBadge value={row["Engine Status"] ? "ON" : "OFF"} />
                    </td>
                    <td>{displayValue(row["Network Signal Strength"])}</td>
                    <td>
                      <StatusBadge value={row["Packet Status"]} />
                    </td>
                    <td>
                      <BoolBadge value={row["GPS Status"]} />
                    </td>
                    <td>
                      <OnOffBadge
                        value={row["Ignition Status"] ? "ON" : "OFF"}
                      />
                    </td>
                    <td>{displayValue(row["Network Provider"])}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={16}>
                    <div className="v-title">
                      <div className="alert alert-danger no_data">
                        Records not found
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && rows.length > 0 && (
          <div className={styles.pageRow}>
            <span className={styles.pageInfo}>
              Showing {rangeStart}-{rangeEnd} of {rows.length}
            </span>
            <div className={styles.pageButtons}>
              <button
                type="button"
                className="fleet-btn fleet-btn-sm gray-theme custom-Btn-animation"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>
              <span className={styles.pageCurrent}>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="fleet-btn fleet-btn-sm gray-theme custom-Btn-animation"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className={styles.disclaimer}>
          <b>Disclaimer:</b> Dashboard data refreshes automatically every 15
          seconds.
        </div>
      </div>
    </div>
  );
}
